import type { AgentRunRow, EventRow, Json } from '../../lib/database.types'
import { clock, shortDate, isSameLocalDay } from '../../lib/time'
import { EMPTY_REGISTRY, inLens, renderEvent, resolve, type Lens, type Registry } from './eventTypes'

/**
 * The activity feed is append-only and quiet. It shows the story: what happened
 * to a piece of work, a gate or a finding, each row said in the registry's
 * words (`public.event_types`, see ./eventTypes).
 *
 * Which rows are story is the registry's call, not a list kept here. The
 * hard-coded exclusion of `watchdog.pass` that stood in for it (DEV-36) is gone,
 * and so are the heartbeats themselves: they live in public.heartbeats. Raw
 * Linear deliveries are left out because the registry calls them integration
 * echo. The `all` lens brings every row back, for someone debugging the
 * plumbing.
 *
 * The webhook rows are still the awkward ones when shown. `summary` describes
 * the issue's state at delivery, so two deliveries a tenth of a second apart
 * read identically -- "DEV-2 ... -> In Review" twice -- while actually reporting
 * different field changes. They are not duplicates and must not be
 * deduplicated: one changed the state, the other changed subscribers. What they
 * need is for the row to say which field moved, which `updatedFrom` carries.
 */

export interface ActivityRow {
  id: number
  time: string
  date: string
  isToday: boolean
  department: string
  text: string
  /** What actually changed, when the event says so. Rendered after the text. */
  changed: string | null
  ref: string
  runId: string | null
  url: string | null
  type: string
  /** The registry's label, or null for a type it does not know. */
  label: string | null
  isMilestone: boolean
}

function obj(value: Json | undefined): Record<string, Json | undefined> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, Json | undefined>)
    : null
}

function str(value: Json | undefined): string | null {
  return typeof value === 'string' && value ? value : null
}

/** Field names Linear reports but nobody needs to read about. */
const NOISE = new Set([
  'updatedAt', 'sortOrder', 'boardOrder', 'prioritySortOrder',
  // Both move as a side effect of a state change and never on their own, so
  // reporting them turns "changed state" into "changed state, started at".
  'startedAt', 'addedToProjectAt',
])

/**
 * Half the feed is webhooks with no run_id, which would leave the identifier
 * column empty on every other row. The issue key is in the Linear url the
 * webhook already carries, so it is recovered rather than shown as a dash.
 */
function refFromUrl(url: string | null): string | null {
  const match = url?.match(/\/issue\/([A-Z][A-Z0-9]*-\d+)/i)
  return match?.[1]?.toUpperCase() ?? null
}

function changedFields(payload: Json): string | null {
  const p = obj(payload)
  const from = p && obj(p['updatedFrom'])
  if (!from) return null
  const fields = Object.keys(from)
    .filter((k) => !NOISE.has(k))
    .map((k) => k.replace(/Id$|Ids$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase())
  if (fields.length === 0) return null
  return `changed ${fields.join(', ')}`
}

export function toActivity(
  events: ReadonlyArray<EventRow>,
  refByRunId: ReadonlyMap<string, { ref: string | null; url: string | null }>,
  registry: Registry = EMPTY_REGISTRY,
  runsById: ReadonlyMap<string, AgentRunRow> = new Map(),
  lens: Lens = 'all',
  now = Date.now(),
): ActivityRow[] {
  const rows: ActivityRow[] = []
  for (const e of events) {
    const res = resolve(registry, e.type)
    // The server filter already did this; repeating it keeps the page honest
    // if the two ever disagree, e.g. a registry that changed between them.
    if (!inLens(res, lens)) continue
    const p = obj(e.payload)
    const linked = e.run_id ? refByRunId.get(e.run_id) : undefined
    // Webhook rows often have no run_id at all -- half of them today -- so the
    // url falls back to whatever the payload itself points at.
    const url = linked?.url ?? str(p?.['url']) ?? str(p?.['pr_url'])
    const def = res.kind === 'event' ? res.def : null
    rows.push({
      id: e.id,
      time: clock(e.ts),
      date: shortDate(e.ts),
      isToday: isSameLocalDay(e.ts, now),
      department: e.department,
      text: def
        ? renderEvent(def, e, e.run_id ? runsById.get(e.run_id) : null)
        : e.summary ?? e.type,
      changed: changedFields(e.payload),
      // A uuid is not a thing a person reads. external_ref is, and it is the
      // same identifier they see in Linear.
      ref: linked?.ref ?? refFromUrl(url) ?? (e.run_id ? e.run_id.slice(0, 8) : '—'),
      runId: e.run_id,
      url,
      type: e.type,
      label: def?.label ?? null,
      isMilestone: def?.isMilestone ?? false,
    })
  }
  return rows
}

export function matchesQuery(row: ActivityRow, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    row.text.toLowerCase().includes(q) ||
    row.department.toLowerCase().includes(q) ||
    row.type.toLowerCase().includes(q) ||
    (row.label ?? '').toLowerCase().includes(q) ||
    row.ref.toLowerCase().includes(q) ||
    (row.changed ?? '').toLowerCase().includes(q)
  )
}
